import { useEffect, useMemo, useRef, useState } from "react";
import { useSnapshot } from "valtio";
import { useParams } from "wouter";
import { app, prefs } from "./state";
import { isOnline, resolveDisplayUnits, type DisplayUnits } from "./utils";

/** Use instead of `prefs.state.units`: the preference alone cannot convert a scene number. */
export function useDisplayUnits(): DisplayUnits {
    const { units } = useSnapshot(prefs.state);
    const { sceneUnit } = useSnapshot(app.state);

    return useMemo(() => resolveDisplayUnits(units, sceneUnit), [units, sceneUnit]);
}

export function useSyncedRef<T extends HTMLInputElement | HTMLTextAreaElement>(value: string) {
    const ref = useRef<T>(null);
    useEffect(() => {
        if (ref.current) {
            ref.current.value = value;
        }
    }, [value]);

    return ref;
}

export const useDebouncedResult = <R>(
    fn: (signal: AbortSignal) => Promise<R>,
    defaultValue: R,
    delay: number = 300,
): R => {
    const [result, setResult] = useState<R>(defaultValue);

    useEffect(() => {
        const controller = new AbortController();

        const timeout = window.setTimeout(async () => {
            try {
                const value = await fn(controller.signal);
                if (!controller.signal.aborted) setResult(value);
            } catch {
                // Keep the previous result
            }
        }, delay);

        return () => {
            controller.abort();
            clearTimeout(timeout);
        };
    }, [fn]);

    return result;
};

export const useMatchMedia = (query: string) => {
    const [matches, setMatches] = useState(() => window.matchMedia(query).matches);

    useEffect(() => {
        const mediaQuery = window.matchMedia(query);
        setMatches(mediaQuery.matches);

        const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
        mediaQuery.addEventListener("change", handler);
        return () => mediaQuery.removeEventListener("change", handler);
    }, [query]);

    return matches;
};

export function useOnline() {
    const [online, setOnline] = useState(isOnline());

    useEffect(() => {
        const update = () => setOnline(isOnline());
        window.addEventListener("online", update);
        window.addEventListener("offline", update);
        return () => {
            window.removeEventListener("online", update);
            window.removeEventListener("offline", update);
        };
    }, []);

    return online;
}

export function useProjectId() {
    const { projectId } = useParams<{ projectId: string }>();
    return projectId;
}

export function useProject() {
    const { projects, datasets } = useSnapshot(app.state);
    const projectId = useProjectId();
    const project = projects[projectId];
    if (!project) throw new Error(`Project ${projectId} no found`);

    return {
        ...projects[projectId],
        datasets: Object.fromEntries(
            Object.entries(datasets).filter(([id]) => {
                return app.projectId(id) === projectId;
            }),
        ),
    };
}
